'use client'

import { StateCreator } from 'zustand'
import { type StoreState } from '.'

export type PersistedCardStatus = 'active' | 'frozen' | 'locked'

export interface PersistedCardSolverConfig {
  cardID: string
  solverType: string
  chainID: number
  cardSolverConfig: Record<string, any>
  status: PersistedCardStatus
  label?: string
  last4?: string
  updatedAt: number
}

type CardSolverConfigsByAddress = Record<string, Record<string, PersistedCardSolverConfig>>

export interface UpsertCardSolverConfigInput {
  cardID: string
  solverType: string
  chainID: number
  cardSolverConfig: Record<string, any>
  status: string
  label?: string | null
  last4?: string | null
  updatedAt?: number
}

export interface CardSolverConfigs {
  byAddress: CardSolverConfigsByAddress
  upsertCardSolverConfig: (address: string, config: UpsertCardSolverConfigInput) => void
  syncWalletCardSolverConfigs: (address: string, configs: UpsertCardSolverConfigInput[]) => void
  removeCardSolverConfig: (address: string, cardID: string) => void
  getWalletCardSolverConfigs: (address: string | undefined) => PersistedCardSolverConfig[]
}

export interface CardSolverConfigsSlice {
  cardSolverConfigs: CardSolverConfigs
}

export interface PersistedCardSolverConfigsSlice {
  cardSolverConfigs: {
    byAddress: CardSolverConfigsByAddress
  }
}

export function getPersistableCardStatus(status: string | undefined): PersistedCardStatus | null {
  const normalized = status?.toLowerCase()
  if (normalized === 'active' || normalized === 'frozen' || normalized === 'locked') {
    return normalized
  }
  return null
}

function normalizeCardSolverConfig(
  config: UpsertCardSolverConfigInput,
  updatedAt = Date.now(),
): PersistedCardSolverConfig | null {
  const status = getPersistableCardStatus(config.status)
  if (!status) return null

  return {
    cardID: config.cardID,
    solverType: config.solverType,
    chainID: config.chainID,
    cardSolverConfig: config.cardSolverConfig,
    status,
    label: config.label ?? undefined,
    last4: config.last4 ?? undefined,
    updatedAt: config.updatedAt ?? updatedAt,
  }
}

function areCardSolverConfigsEqual(a: PersistedCardSolverConfig | undefined, b: PersistedCardSolverConfig): boolean {
  if (!a) return false

  return (
    a.cardID === b.cardID &&
    a.solverType === b.solverType &&
    a.chainID === b.chainID &&
    a.status === b.status &&
    a.label === b.label &&
    a.last4 === b.last4 &&
    JSON.stringify(a.cardSolverConfig) === JSON.stringify(b.cardSolverConfig)
  )
}

function areWalletCardSolverConfigsEqual(
  a: Record<string, PersistedCardSolverConfig>,
  b: Record<string, PersistedCardSolverConfig>,
): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false

  return bKeys.every((cardID) => areCardSolverConfigsEqual(a[cardID], b[cardID]!))
}

export const initCardSolverConfigsSlice: StateCreator<StoreState, [], [], CardSolverConfigsSlice> = (set, get) => ({
  cardSolverConfigs: {
    byAddress: {},
    upsertCardSolverConfig: (address, config) =>
      set((state) => {
        const normalized = normalizeCardSolverConfig(config)
        const addressKey = address.toLowerCase()
        const existingByAddress = state.cardSolverConfigs.byAddress[addressKey] ?? {}

        if (!normalized) {
          if (!existingByAddress[config.cardID]) {
            return state
          }
          const { [config.cardID]: _, ...rest } = existingByAddress
          return {
            cardSolverConfigs: {
              ...state.cardSolverConfigs,
              byAddress: {
                ...state.cardSolverConfigs.byAddress,
                [addressKey]: rest,
              },
            },
          }
        }

        const existingConfig = existingByAddress[normalized.cardID]
        if (areCardSolverConfigsEqual(existingConfig, normalized)) {
          return state
        }

        return {
          cardSolverConfigs: {
            ...state.cardSolverConfigs,
            byAddress: {
              ...state.cardSolverConfigs.byAddress,
              [addressKey]: {
                ...existingByAddress,
                [normalized.cardID]: normalized,
              },
            },
          },
        }
      }),
    syncWalletCardSolverConfigs: (address, configs) =>
      set((state) => {
        const addressKey = address.toLowerCase()
        const nextConfigs: Record<string, PersistedCardSolverConfig> = {}

        for (const config of configs) {
          const existingConfig = state.cardSolverConfigs.byAddress[addressKey]?.[config.cardID]
          const normalized = normalizeCardSolverConfig(config, existingConfig?.updatedAt ?? Date.now())
          if (normalized) {
            nextConfigs[normalized.cardID] = areCardSolverConfigsEqual(existingConfig, normalized)
              ? existingConfig!
              : { ...normalized, updatedAt: config.updatedAt ?? Date.now() }
          }
        }

        const existingByAddress = state.cardSolverConfigs.byAddress[addressKey] ?? {}
        if (areWalletCardSolverConfigsEqual(existingByAddress, nextConfigs)) {
          return state
        }

        return {
          cardSolverConfigs: {
            ...state.cardSolverConfigs,
            byAddress: {
              ...state.cardSolverConfigs.byAddress,
              [addressKey]: nextConfigs,
            },
          },
        }
      }),
    removeCardSolverConfig: (address, cardID) =>
      set((state) => {
        const addressKey = address.toLowerCase()
        if (!state.cardSolverConfigs.byAddress[addressKey]?.[cardID]) {
          return state
        }
        const { [cardID]: _, ...rest } = state.cardSolverConfigs.byAddress[addressKey] ?? {}

        return {
          cardSolverConfigs: {
            ...state.cardSolverConfigs,
            byAddress: {
              ...state.cardSolverConfigs.byAddress,
              [addressKey]: rest,
            },
          },
        }
      }),
    getWalletCardSolverConfigs: (address) => {
      if (!address) return []
      return Object.values(get().cardSolverConfigs.byAddress[address.toLowerCase()] ?? {})
    },
  },
})

export function persistCardSolverConfigsSlice(state: StoreState): PersistedCardSolverConfigsSlice {
  return {
    cardSolverConfigs: {
      byAddress: state.cardSolverConfigs.byAddress,
    },
  }
}
